/**
 * AP charge accounts: the expense headings a payable can be charged to when it
 * does not belong to a project.
 *
 * These are the same list the Parameters screen calls "Charge Accounts" — one
 * source of truth, given its own page here because it is the one list finance
 * actually maintains.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCircle2, Pencil, Plus, Wallet } from 'lucide-react';
import { ApiError, api } from '../../lib/api';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface Value {
  id: string;
  code: string;
  label: string;
  sortOrder: number;
  active: boolean;
}

interface Group {
  id: string;
  key: string;
  label: string;
  values: Value[];
}

export function ApAccountsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Value | null>(null);
  const [draft, setDraft] = useState({ code: '', label: '', sortOrder: '0' });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const parameters = useQuery({
    queryKey: ['parameters'],
    queryFn: () => api<{ data: Group[] }>('/parameters'),
  });

  const group = parameters.data?.data.find((row) => row.key === 'charge_account');
  const rows = [...(group?.values ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code),
  );

  const close = (): void => {
    setAdding(false);
    setEditing(null);
    setDraft({ code: '', label: '', sortOrder: '0' });
    setError(null);
  };

  const done = (message: string) => (): void => {
    void queryClient.invalidateQueries({ queryKey: ['parameters'] });
    setNotice(message);
    close();
  };

  const save = useMutation({
    mutationFn: () =>
      editing
        ? api(`/parameters/values/${editing.id}`, {
            method: 'PATCH',
            body: { label: draft.label.trim(), sortOrder: Number(draft.sortOrder || 0) },
          })
        : api(`/parameters/groups/${group?.id}/values`, {
            method: 'POST',
            body: {
              code: draft.code.trim(),
              label: draft.label.trim(),
              sortOrder: Number(draft.sortOrder || 0),
            },
          }),
    onSuccess: done(editing ? 'Account updated.' : 'Account added.'),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the account.'),
  });

  const toggle = useMutation({
    mutationFn: (row: Value) =>
      api(`/parameters/values/${row.id}`, { method: 'PATCH', body: { active: !row.active } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['parameters'] });
      setNotice('Account status changed.');
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not change the account.'),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">AP accounts</h1>
          <p className="text-sm text-slate-500">
            What a payable is charged to when it is an overhead rather than a project cost. An
            account in use is switched off, never deleted, so old entries keep their heading.
          </p>
        </div>
        <Button
          onClick={() => {
            setDraft({ code: '', label: '', sortOrder: String(rows.length * 10) });
            setAdding(true);
          }}
          disabled={!group}
        >
          <Plus aria-hidden="true" className="h-4 w-4" /> Add account
        </Button>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && !adding && !editing && <Alert tone="critical">{error}</Alert>}

      {parameters.isLoading && <Skeleton className="h-64 w-full" />}

      {!parameters.isLoading && rows.length === 0 && (
        <Card>
          <EmptyState
            title="No AP accounts yet"
            description="Add the expense headings finance uses, for example 6100 Office Supplies."
          />
        </Card>
      )}

      {rows.length > 0 && (
        <Card>
          <TableWrap>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Account</Th>
                <Th align="right">Order</Th>
                <Th>Status</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((row) => (
                <tr key={row.id}>
                  <Td>
                    <span className="font-mono text-xs">{row.code}</span>
                  </Td>
                  <Td>{row.label}</Td>
                  <Td align="right">
                    <span className="text-xs text-slate-500">{row.sortOrder}</span>
                  </Td>
                  <Td>
                    <StatusChip tone={row.active ? 'ok' : 'neutral'}>
                      {row.active ? 'In use' : 'Switched off'}
                    </StatusChip>
                  </Td>
                  <Td>
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        aria-label={`Edit ${row.label}`}
                        onClick={() => {
                          setDraft({
                            code: row.code,
                            label: row.label,
                            sortOrder: String(row.sortOrder),
                          });
                          setEditing(row);
                        }}
                      >
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        aria-label={row.active ? `Switch off ${row.label}` : `Switch on ${row.label}`}
                        onClick={() => toggle.mutate(row)}
                      >
                        {row.active ? (
                          <Ban aria-hidden="true" className="h-4 w-4" />
                        ) : (
                          <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}

      {(adding || editing) && (
        <Modal
          title={editing ? `Edit ${editing.code}` : 'Add AP account'}
          description="The code is what appears on reports; it cannot be changed once saved."
          onClose={close}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              save.mutate();
            }}
          >
            {error && <Alert tone="critical">{error}</Alert>}
            <Field label="Code" required hint="For example 6100">
              <input
                className={inputClass}
                value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
                required
                disabled={editing !== null}
                autoFocus={editing === null}
              />
            </Field>
            <Field label="Account name" required hint="For example 6100 Office Supplies">
              <input
                className={inputClass}
                value={draft.label}
                onChange={(event) => setDraft({ ...draft, label: event.target.value })}
                required
                autoFocus={editing !== null}
              />
            </Field>
            <Field label="Sort order" hint="Lower numbers appear first in the dropdown">
              <input
                className={`${inputClass} w-32`}
                type="number"
                value={draft.sortOrder}
                onChange={(event) => setDraft({ ...draft, sortOrder: event.target.value })}
              />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={save.isPending || !draft.code.trim() || !draft.label.trim()}
              >
                <Wallet aria-hidden="true" className="h-4 w-4" />
                {editing ? 'Save changes' : 'Add account'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
